import { useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { GiftCardPurchase } from "@/components/GiftCardPurchase";
import { MapPin, Phone, Mail, Clock, Calendar, ArrowRight, ShoppingBag, Sparkles, Globe, Link2 as LinkIcon, Star, TicketPercent, MessageSquare, MessageSquareQuote } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import BookingModal from "@/components/BookingModal";
import CatalogImage from "@/components/CatalogImage";
import InquiryModal from "@/components/InquiryModal";
import EventModal from "@/components/EventModal";
import StorefrontPackages from "@/components/StorefrontPackages";
import { LocationBadge } from "@/components/LocationBadge";
import { ShareButton } from "@/components/ShareButton";
import { SocialShare } from "@/components/SocialShare";
import Seo, { stripHtml } from "@/components/Seo";
import { toast } from "sonner";
import { fmtDate, fmtTimeRange, money } from "@/lib/format";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function embedUrl(url) {
  if (!url) return "";
  const yt = String(url).match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vim = String(url).match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vim) return `https://player.vimeo.com/video/${vim[1]}`;
  return "";
}

const CatChip = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${active ? "bg-[#144F75] text-white" : "bg-white border border-[#144F75]/10 text-[#4A5D6B] hover:border-[#144F75]/40"}`}>
    {children}
  </button>
);

export default function LocationStorefront() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { add } = useCart();
  const [bookSvc, setBookSvc] = useState(null);
  const [eventModal, setEventModal] = useState(null);
  const [svcCat, setSvcCat] = useState("all");
  const [evtCat, setEvtCat] = useState("all");
  const [contactOpen, setContactOpen] = useState(false);

  const { data: loc, isLoading, isError } = useQuery({
    queryKey: ["location-storefront", slug],
    queryFn: () => api.get(`/locations/${slug}`).then((r) => r.data),
    retry: false,
  });
  const { data: promos = [] } = useQuery({
    queryKey: ["location-promos", slug],
    queryFn: () => api.get(`/locations/${slug}/promotions`).then((r) => r.data),
    retry: false,
  });

  if (isLoading) return <div className="min-h-[60vh] grid place-items-center text-[#4A5D6B]">Loading center…</div>;
  if (isError || !loc) return (
    <div className="min-h-[60vh] grid place-items-center text-center px-6">
      <div><h1 className="font-serif text-3xl text-[#1A242B] mb-2">Center not found</h1>
        <Link to="/locations" className="eyw-btn !py-2 text-sm mt-4">Browse all centers</Link></div>
    </div>
  );

  const svcCats = [...new Set((loc.services || []).map((s) => s.category).filter(Boolean))];
  const evtCats = [...new Set((loc.events || []).map((e) => e.category).filter(Boolean))];
  const shownServices = (loc.services || []).filter((s) => svcCat === "all" || s.category === svcCat);
  const shownEvents = (loc.events || []).filter((e) => evtCat === "all" || e.category === evtCat);

  return (
    <div data-testid="location-storefront">
      <Seo title={loc.name} description={stripHtml(loc.description || "") || `${loc.name} — a wellness center in the Enhancing Your World ecosystem.`}
        image={loc.image} path={`/locations/${loc.slug || slug}`}
        jsonLd={{
          "@context": "https://schema.org", "@type": "HealthAndBeautyBusiness", name: loc.name,
          description: stripHtml(loc.description || "", 300) || undefined,
          image: loc.image || undefined, telephone: loc.phone || undefined, email: loc.email || undefined,
          url: `${window.location.origin}/locations/${loc.slug || slug}`,
          address: (loc.address || loc.city) ? {
            "@type": "PostalAddress", streetAddress: loc.address || undefined,
            addressLocality: loc.city || undefined, addressRegion: loc.state || undefined, postalCode: loc.zip || undefined,
          } : undefined,
          aggregateRating: loc.google_rating ? {
            "@type": "AggregateRating", ratingValue: loc.google_rating,
            reviewCount: loc.google_review_count || 1, bestRating: 5,
          } : undefined,
        }} />
      <InquiryModal open={contactOpen} onClose={() => setContactOpen(false)} targetType="center" targetId={loc.id || slug} targetName={loc.name} />
      {/* Hero */}
      <div className="relative bg-[#144F75] text-[#F9F7F3]">
        {(loc.image || loc.gallery?.[0]) && <img src={loc.image || loc.gallery[0]} alt={loc.name} className="absolute inset-0 w-full h-full object-cover opacity-25" />}
        <div className="relative max-w-[1200px] mx-auto px-6 lg:px-10 py-20">
          <LocationBadge kind={loc.kind} name={loc.kind === "flagship" ? `${loc.name} · Flagship` : loc.kind === "virtual" ? "Virtual" : "Affiliate Center"} />
          <h1 className="font-serif text-4xl sm:text-5xl mt-3 mb-3 flex items-center gap-2"><MapPin size={30} className="text-[#D98E73]" /> {loc.name}</h1>
          {(loc.address || loc.city) && <p className="text-white/80">{[loc.address, loc.city, loc.state, loc.zip].filter(Boolean).join(", ")}</p>}
          {loc.description && <p className="text-white/80 max-w-2xl mt-3">{loc.description.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().slice(0, 240)}</p>}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button onClick={() => setContactOpen(true)} data-testid="location-contact-btn" className="eyw-btn !bg-[#D98E73] hover:!bg-[#c67a60] !py-2 text-sm"><MessageSquareQuote size={15} /> Ask a Question</button>
            {user && loc.manager_id && user.id !== loc.manager_id && (
              <button onClick={() => nav(`/messages?to=${loc.manager_id}`)} data-testid="location-message-btn" className="eyw-btn-ghost !py-2 text-sm border border-white/40 text-white hover:bg-white/10"><MessageSquare size={15} /> Message</button>
            )}
            <SocialShare path={`/locations/${slug}`} ogPath={`/api/share/location/${slug}`} title={loc.name} testid="location-share" label="Share this center" dark />
          </div>
          <div className="flex flex-wrap gap-4 mt-5 text-sm text-white/85">
            {loc.phone && <span className="flex items-center gap-2"><Phone size={15} /> {loc.phone}</span>}
            {loc.email && <span className="flex items-center gap-2"><Mail size={15} /> {loc.email}</span>}
          </div>
          {(loc.website || Object.values(loc.social_links || {}).some(Boolean)) && (
            <div className="flex flex-wrap gap-2 mt-4" data-testid="location-social-links">
              {loc.website && <a href={loc.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-white/40 text-white px-3 py-1.5 text-xs hover:bg-white/10"><Globe size={14} /> Website</a>}
              {Object.entries(loc.social_links || {}).filter(([, v]) => v).map(([k, v]) => (
                <a key={k} href={v} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-white/40 text-white px-3 py-1.5 text-xs capitalize hover:bg-white/10"><LinkIcon size={14} /> {k}</a>
              ))}
            </div>
          )}
        </div>
      </div>

      {(loc.gallery?.length > 0 || loc.amenities?.length > 0) && (
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 pt-10" data-testid="location-gallery-section">
          {loc.gallery?.length > 0 && (
            <div className="flex gap-3 overflow-x-auto snap-x pb-2 sm:grid sm:grid-cols-3 lg:grid-cols-4 sm:overflow-visible" data-testid="location-gallery">
              {loc.gallery.map((u, i) => <img key={i} src={u} alt={`${loc.name} photo ${i + 1}`} loading="lazy" className="h-44 w-64 sm:w-full shrink-0 snap-start object-cover rounded-2xl" data-testid={`location-gallery-${i}`} />)}
            </div>
          )}
          {loc.amenities?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4" data-testid="location-amenities">
              {loc.amenities.map((a) => <span key={a} className="rounded-full bg-[#144F75]/[0.07] text-[#144F75] text-xs px-3 py-1.5">{a}</span>)}
            </div>
          )}
        </div>
      )}

      {promos.length > 0 && (
        <div className="bg-[#D98E73]/15 border-y border-[#D98E73]/30" data-testid="center-promo-banner">
          <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-3.5 flex flex-wrap items-center gap-x-6 gap-y-1.5">
            {promos.map((p, i) => (
              <p key={i} className="text-sm text-[#8A4B33] flex items-center gap-2" data-testid={`center-promo-${i}`}>
                <TicketPercent size={16} className="text-[#B5794E] shrink-0" />
                <span>
                  <strong>{p.name}</strong> — {p.discount_type === "percent" ? `${p.value}% off` : `${money(p.value)} off`}
                  {p.applies_to === "services" ? " services" : p.applies_to === "events" ? " events & classes" : p.applies_to === "merch" ? " merchandise" : " everything"}
                  {p.kind === "membership" && p.membership_tier ? ` for ${p.membership_tier.charAt(0).toUpperCase() + p.membership_tier.slice(1)} members` : ""}
                  {p.expires_at ? ` · through ${p.expires_at}` : ""} — applied automatically at checkout
                </span>
              </p>
            ))}
          </div>
        </div>
      )}

      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16 space-y-16">
        {/* Services */}
        {loc.services?.length > 0 && (
          <section data-testid="storefront-services">
            <h2 className="font-serif text-3xl text-[#1A242B] mb-6">Book a service at this center</h2>
            {svcCats.length > 1 && (
              <div className="flex flex-wrap gap-2 mb-6" data-testid="storefront-service-filters">
                <CatChip active={svcCat === "all"} onClick={() => setSvcCat("all")} testid="storefront-svc-cat-all">All</CatChip>
                {svcCats.map((c) => (
                  <CatChip key={c} active={svcCat === c} onClick={() => setSvcCat(c)} testid={`storefront-svc-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}>{c}</CatChip>
                ))}
              </div>
            )}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {shownServices.map((s) => (
                <div key={s.id} className="eyw-card overflow-hidden" data-testid={`storefront-service-${s.id}`}>
                  <CatalogImage src={s.image} alt={s.title} testid={`storefront-service-image-${s.id}`} />
                  <div className="p-6">
                    <p className="eyw-label mb-1">{s.category}</p>
                    <h3 className="font-serif text-xl text-[#1A242B]">{s.title}</h3>
                    <p className="text-sm text-[#4A5D6B] mt-2 line-clamp-2">{s.short_description}</p>
                    <div className="flex items-center justify-between mt-4">
                      <span className="text-[#144F75] font-serif text-lg">{s.price > 0 ? `${money(s.price)}` : "Free"} <span className="text-xs text-[#8FA89B]">· {s.duration_min}min</span></span>
                      <button onClick={() => setBookSvc(s)} data-testid={`storefront-book-${s.id}`} className="eyw-btn !py-2 !px-4 text-sm">Book</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Events / Retreats */}
        {loc.events?.length > 0 && (
          <section data-testid="storefront-events">
            <h2 className="font-serif text-3xl text-[#1A242B] mb-6">Events & retreats here</h2>
            {evtCats.length > 1 && (
              <div className="flex flex-wrap gap-2 mb-6" data-testid="storefront-event-filters">
                <CatChip active={evtCat === "all"} onClick={() => setEvtCat("all")} testid="storefront-evt-cat-all">All</CatChip>
                {evtCats.map((c) => (
                  <CatChip key={c} active={evtCat === c} onClick={() => setEvtCat(c)} testid={`storefront-evt-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}>{c}</CatChip>
                ))}
              </div>
            )}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {shownEvents.map((e) => (
                <div key={e.id} className="eyw-card p-6" data-testid={`storefront-event-${e.id}`}>
                  <p className="eyw-label mb-1">{e.category}</p>
                  <h3 className="font-serif text-xl text-[#1A242B]">{e.title}</h3>
                  <p className="text-sm text-[#4A5D6B] mt-2 flex items-center gap-2"><Calendar size={14} /> {fmtDate(e.date)} · {fmtTimeRange(e.start_time)}</p>
                  <div className="flex items-center justify-between mt-4">
                    <span className="text-[#144F75] font-serif text-lg">{e.price > 0 ? `${money(e.price)}` : "Free"}</span>
                    <button onClick={() => setEventModal(e)} data-testid={`storefront-register-${e.id}`} className="eyw-btn !py-2 !px-4 text-sm">Register</button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Enhancers */}
        {loc.enhancers?.length > 0 && (
          <section data-testid="storefront-enhancers">
            <h2 className="font-serif text-3xl text-[#1A242B] mb-6">Enhancers at this center</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {loc.enhancers.map((en) => (
                <Link to={`/enhancers/${en.id}`} key={en.id} className="eyw-card p-6 text-center hover:shadow-lg transition-shadow" data-testid={`storefront-enhancer-${en.id}`}>
                  {en.avatar ? <img src={en.avatar} alt={en.name} className="h-20 w-20 rounded-full object-cover mx-auto mb-3" /> : <div className="h-20 w-20 rounded-full bg-[#144F75]/10 grid place-items-center mx-auto mb-3"><Sparkles className="text-[#144F75]" /></div>}
                  <h3 className="font-serif text-lg text-[#1A242B]">{en.name}</h3>
                  {en.headline && <p className="text-xs text-[#4A5D6B] mt-1">{en.headline}</p>}
                  <span className="mt-3 inline-block text-sm font-semibold text-[#144F75]" data-testid={`enhancer-card-cta-${en.id}`}>View profile & book →</span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Products */}
        {loc.products?.length > 0 && (
          <section data-testid="storefront-products">
            <h2 className="font-serif text-3xl text-[#1A242B] mb-6">Shop from this center</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {loc.products.map((p) => (
                <div key={p.id} className="eyw-card overflow-hidden" data-testid={`storefront-product-${p.id}`}>
                  {p.image && <img src={p.image} alt={p.title} className="h-36 w-full object-cover" />}
                  <div className="p-5">
                    <h3 className="text-[#1A242B] font-medium">{p.title}</h3>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-[#144F75] font-serif">{money(p.price)}</span>
                      <button onClick={() => { add({ id: p.id, title: p.title, price: p.price, type: p.type, image: p.image }, 1); toast.success("Added to cart"); }} className="eyw-btn-ghost !py-1.5 !px-3 text-xs"><ShoppingBag size={13} /> Add</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="mt-2"><StorefrontPackages packages={loc.packages} title="Session packages at this center" /></div>

        {/* Links & Media */}
        {((loc.videos || []).map(embedUrl).some(Boolean) || (loc.channel_videos || []).length > 0) && (
          <section data-testid="storefront-media">
            <h2 className="font-serif text-3xl text-[#1A242B] mb-6">Watch & connect</h2>
            {(loc.videos || []).map(embedUrl).some(Boolean) && (
              <div className="grid md:grid-cols-2 gap-6 mb-8" data-testid="storefront-curated-videos">
                {(loc.videos || []).map((v, i) => {
                  const src = embedUrl(v);
                  if (!src) return null;
                  return (
                    <div key={i} className="aspect-video rounded-xl overflow-hidden bg-black/5" data-testid={`storefront-video-${i}`}>
                      <iframe src={src} title={`Video ${i + 1}`} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                    </div>
                  );
                })}
              </div>
            )}
            {(loc.channel_videos || []).length > 0 && (
              <div data-testid="storefront-channel-videos">
                <h3 className="font-serif text-xl text-[#1A242B] mb-4">Latest from our channel</h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {loc.channel_videos.map((cv) => (
                    <a key={cv.video_id} href={cv.url} target="_blank" rel="noreferrer" className="eyw-card overflow-hidden group" data-testid={`storefront-channel-video-${cv.video_id}`}>
                      {cv.thumbnail && <div className="aspect-video overflow-hidden"><img src={cv.thumbnail} alt={cv.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" /></div>}
                      <div className="p-4"><p className="text-sm text-[#1A242B] font-medium line-clamp-2">{cv.title}</p></div>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {/* Testimonials */}
        <CenterTestimonials centerName={loc.name} />

        {/* Rooms & Hours */}
        <section className="grid md:grid-cols-2 gap-8">
          {loc.rooms?.length > 0 && (
            <div className="eyw-card p-8" data-testid="storefront-rooms">
              <h2 className="font-serif text-2xl text-[#1A242B] mb-4">Our spaces</h2>
              <ul className="space-y-3">
                {loc.rooms.map((r, i) => (
                  <li key={i} className="border-b border-[#E5E0D8] pb-3 last:border-0">
                    <div className="font-medium text-[#1A242B]">{r.name} {r.capacity > 0 && <span className="text-xs text-[#8FA89B]">· up to {r.capacity}</span>}</div>
                    {r.description && <p className="text-sm text-[#4A5D6B]">{r.description}</p>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {loc.hours?.length > 0 && (
            <div className="eyw-card p-8" data-testid="storefront-hours">
              <h2 className="font-serif text-2xl text-[#1A242B] mb-4 flex items-center gap-2"><Clock size={20} /> Hours</h2>
              <ul className="space-y-1.5">
                {loc.hours.map((h) => (
                  <li key={h.day} className="flex justify-between text-sm text-[#4A5D6B] border-b border-[#F1EDE6] pb-1.5"><span>{DAYS[h.day]}</span><span>{h.closed ? "Closed" : `${h.open} – ${h.close}`}</span></li>
                ))}
              </ul>
            </div>
          )}
        </section>

        <section className="mb-14 max-w-2xl" data-testid="center-gift-cards">
          <GiftCardPurchase locationId={loc.id} title={`Gift card from ${loc.name}`} testid="giftcert-card" />
        </section>

        <div className="text-center">
          <Link to="/locations" className="inline-flex items-center gap-1 text-[#144F75] text-sm font-medium hover:gap-2 transition-all">
            <ArrowRight size={15} className="rotate-180" /> All centers
          </Link>
        </div>
      </div>

      {bookSvc && <BookingModal service={bookSvc} onClose={() => setBookSvc(null)} />}
      {eventModal && <EventModal event={eventModal} onClose={() => setEventModal(null)} />}
    </div>
  );
}

function CenterTestimonials({ centerName }) {
  const { data: all = [] } = useQuery({ queryKey: ["testimonials"], queryFn: () => api.get("/testimonials").then((r) => r.data) });
  const items = all
    .filter((t) => t.published && (t.status || "approved") === "approved")
    .filter((t) => t.subject_name === centerName || t.subject_type === "ecosystem" || t.featured)
    .sort((a, b) => (b.subject_name === centerName) - (a.subject_name === centerName))
    .slice(0, 3);
  if (!items.length) return null;
  return (
    <section data-testid="storefront-testimonials">
      <h2 className="font-serif text-3xl text-[#1A242B] mb-6">Kind words from our community</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((t) => (
          <figure key={t.id} className="eyw-card p-6 flex flex-col" data-testid={`storefront-testimonial-${t.id}`}>
            <div className="flex mb-2">{[1, 2, 3, 4, 5].map((n) => <Star key={n} size={13} className={n <= (t.rating || 5) ? "fill-[#D98E73] text-[#D98E73]" : "text-[#E5E0D8]"} />)}</div>
            <blockquote className="text-sm text-[#4A5D6B] leading-relaxed flex-1">"{t.quote}"</blockquote>
            <figcaption className="text-xs text-[#1A242B] font-medium mt-3">{t.name}{t.role ? <span className="text-[#8FA89B] font-normal"> · {t.role}</span> : null}</figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}
